import { Box, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import { useTranslation } from "next-i18next";
import React, { useState } from "react";

export type MonthValue = {
	year: number;
	month: number; // 1-12
};

export type MonthSliderProps = {
	value: MonthValue;
	onChange: (value: MonthValue) => void;
	disabled?: boolean;
};

export function MonthSlider({ value, onChange, disabled = false }: MonthSliderProps) {
	const { i18n } = useTranslation();
	const [isPickerOpen, setIsPickerOpen] = useState(false);
	const anchorRef = React.useRef<HTMLDivElement>(null);

	// Convert MonthValue to dayjs object
	const currentMonth: Dayjs = dayjs()
		.year(value.year)
		.month(value.month - 1);

	const handlePrevious = () => {
		if (disabled) return;
		const previousMonth = currentMonth.subtract(1, "month");
		onChange({
			year: previousMonth.year(),
			month: previousMonth.month() + 1, // dayjs uses 0-indexed months
		});
	};

	const handleNext = () => {
		if (disabled) return;
		const nextMonth = currentMonth.add(1, "month");
		onChange({
			year: nextMonth.year(),
			month: nextMonth.month() + 1, // dayjs uses 0-indexed months
		});
	};

	const handleDateChange = (newDate: Dayjs | null) => {
		if (newDate && !disabled) {
			onChange({
				year: newDate.year(),
				month: newDate.month() + 1, // dayjs uses 0-indexed months
			});
		}
		setIsPickerOpen(false);
	};

	const handleMonthClick = () => {
		if (!disabled) {
			setIsPickerOpen(true);
		}
	};

	// Format the month display according to locale
	const locale = i18n.language ?? "de";
	const formattedMonth = currentMonth.locale(locale).format("MMMM YYYY");

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
				}}>
				<IconButton onClick={handlePrevious} disabled={disabled} size="small">
					<ChevronLeft />
				</IconButton>
				<Box ref={anchorRef}>
					<Typography
						variant="body1"
						onClick={handleMonthClick}
						sx={{
							minWidth: "150px",
							textAlign: "center",
							userSelect: "none",
							cursor: disabled ? "default" : "pointer",
							"&:hover": {
								backgroundColor: disabled ? "transparent" : "action.hover",
								borderRadius: 1,
							},
							padding: "4px 8px",
							transition: "background-color 0.2s",
						}}>
						{formattedMonth}
					</Typography>
				</Box>
				<IconButton onClick={handleNext} disabled={disabled} size="small">
					<ChevronRight />
				</IconButton>

				{/* Hidden DatePicker that opens as a popup */}
				<DatePicker
					value={currentMonth}
					onChange={handleDateChange}
					open={isPickerOpen}
					onClose={() => setIsPickerOpen(false)}
					onOpen={() => setIsPickerOpen(true)}
					views={["month", "year"]}
					disabled={disabled}
					slotProps={{
						textField: {
							sx: { display: "none" }, // Hide the input field
						},
						popper: {
							placement: "bottom-start",
							anchorEl: anchorRef.current,
						},
					}}
				/>
			</Box>
		</LocalizationProvider>
	);
}
